import { useMemo } from 'react';
import { Actions } from '../../components/product-table/columns/actions-column';
import { ExtendedCost } from '../../components/product-table/columns/extended-cost-column';
import { ItemCode } from '../../components/product-table/columns/item-code-column';
import { ProductNameWithLabels } from '../../components/product-table/columns/product-name-with-labels-column';
import { QOH } from '../../components/product-table/columns/qoh-column';
import { Reorder } from '../../components/product-table/columns/reorder-column';
import { getUnitsSoldColumn } from '../../components/product-table/columns/sales-columns';
import { Size } from '../../components/product-table/columns/size-column';
import { Type } from '../../components/product-table/columns/type-column';
import { VendorName } from '../../components/product-table/columns/vendor-name-column';
import { FiltersPopover } from '../../components/product-table/filters/filters-popover';
import { HideDisabledFilter } from '../../components/product-table/filters/hide-disabled-filter';
import { NotSoldInFilter } from '../../components/product-table/filters/not-sold-in-filter';
import { SoldInFilter } from '../../components/product-table/filters/sold-in-filter';
import { TotalValue } from '../../components/product-table/filters/total-value-filter';
import type { StrictColumnDef } from '../../components/tanstack-table/column';
import { ReportActions } from '../../components/tanstack-table/report-actions';
import { TanStackTable } from '../../components/tanstack-table/table';
import { useRouteRowSelection } from '../../components/tanstack-table/use-route-row-selection';
import { useTableRef } from '../../components/tanstack-table/use-table-ref';
import { PageToolbar } from '../../components/toolbar/page-toolbar';
import { SearchWithCount } from '../../components/toolbar/search-with-count';
import { ProductTableContext } from '../../contexts/product-table-context';
import type { Product, SaleStatDays } from '../../data/product';
import { type Filters, useFilteredProducts } from '../../hooks/products';
import { productReportsLayout } from '../../router';

const defaultFilters: Filters = {
  soldIn: 365,
  notSoldIn: 180,
  minimumInventory: 1,
};

const soldInDays: SaleStatDays[] = [90, 180, 365];
const notSoldInDays: SaleStatDays[] = [30, 60, 90, 180];

export function UnderperformingInventoryReport() {
  const products = productReportsLayout.useLoaderData();
  const { filters, setFilters, filteredProducts, baselineCount } = useFilteredProducts(
    products,
    defaultFilters
  );
  const { rowSelection, onRowSelectionChange, onRowClick } = useRouteRowSelection();
  const [table, onTableReady] = useTableRef<Product>();

  const columns = useMemo<StrictColumnDef<Product>[]>(() => {
    const soldInColumn = getUnitsSoldColumn(filters.soldIn || 365);
    const notSoldInColumn = getUnitsSoldColumn(filters.notSoldIn || 180);

    return [
      VendorName,
      ItemCode,
      ProductNameWithLabels,
      Type,
      Size,
      soldInColumn,
      notSoldInColumn,
      QOH,
      Reorder,
      ExtendedCost,
      Actions,
    ];
  }, [filters.soldIn, filters.notSoldIn]);

  return (
    <>
      <ProductTableContext.Provider value={{ filters, setFilters, filteredProducts }}>
        <PageToolbar
          left={
            <FiltersPopover defaultFilters={defaultFilters}>
              <HideDisabledFilter />
              <SoldInFilter overrideSoldInDays={soldInDays} />
              <NotSoldInFilter overrideSoldInDays={notSoldInDays} />
            </FiltersPopover>
          }
          right={
            <>
              <TotalValue />
              <SearchWithCount
                quantity={filteredProducts.length}
                total={baselineCount}
                noun="products"
              />
              <ReportActions table={table} title="Underperforming Inventory" />
            </>
          }
        />
      </ProductTableContext.Provider>
      <TanStackTable
        data={filteredProducts}
        columns={columns}
        rowSelection={rowSelection}
        onRowSelectionChange={onRowSelectionChange}
        onRowClick={onRowClick}
        getRowId={(product) => product.id}
        onTableReady={onTableReady}
      />
    </>
  );
}
